import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { FilmTitle } from "@/utils/catalog";
import { formatViews } from "@/utils/catalog";

interface HeroCarouselProps {
  items: FilmTitle[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const timer = useRef<number | null>(null);
  const touchStart = useRef<number | null>(null);

  const go = useCallback(
    (next: number) => {
      if (items.length === 0) return;
      setActive(((next % items.length) + items.length) % items.length);
    },
    [items.length],
  );

  useEffect(() => {
    if (items.length <= 1) return;
    timer.current = window.setInterval(() => {
      setActive((v) => (v + 1) % items.length);
    }, 6000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [items.length]);

  const resetTimer = () => {
    if (timer.current) window.clearInterval(timer.current);
    timer.current = window.setInterval(() => {
      setActive((v) => (v + 1) % items.length);
    }, 6000);
  };

  if (items.length === 0) return null;
  const current = items[active];

  return (
    <section
      className="relative w-full h-[520px] md:h-[640px] overflow-hidden bg-primary-950"
      onTouchStart={(e) => {
        touchStart.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStart.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(delta) > 50) {
          go(active + (delta < 0 ? 1 : -1));
          resetTimer();
        }
        touchStart.current = null;
      }}
    >
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === active ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 焦点海报`}
            title={`${item.title} - ${item.genres.join("、")} - CC视频`}
            className={`w-full h-full object-cover object-top ${
              i === active ? "cc-kenburns" : ""
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-transparent to-primary-950/40" />
        </div>
      ))}

      <div className="relative z-10 h-full w-full px-4 md:px-6 flex items-end md:items-center">
        <div className="pb-24 md:pb-0 max-w-xl" key={current.id}>
          <div className="flex items-center gap-2 mb-4 cc-fade-up">
            <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-accent-500 text-primary-950">
              {current.kind === "movie" ? "热门电影" : "热播剧集"}
            </span>
            <span className="px-2 py-0.5 rounded text-[11px] border border-background-50/30 text-background-200">
              {current.year}
            </span>
            <span className="px-2 py-0.5 rounded text-[11px] border border-background-50/30 text-background-200">
              {current.region}
            </span>
          </div>
          <h1
            className="font-heading text-3xl md:text-5xl font-black text-background-50 leading-tight tracking-tight cc-fade-up"
            style={{ animationDelay: "60ms" }}
          >
            {current.title}
          </h1>
          <p
            className="mt-2 text-sm text-background-300 font-label tracking-wide cc-fade-up"
            style={{ animationDelay: "90ms" }}
          >
            {current.origTitle}
          </p>
          <p
            className="mt-4 text-sm md:text-base text-background-200 leading-relaxed line-clamp-3-custom max-w-lg cc-fade-up"
            style={{ animationDelay: "140ms" }}
          >
            {current.synopsis}
          </p>
          <div
            className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-background-300 cc-fade-up"
            style={{ animationDelay: "180ms" }}
          >
            <span className="flex items-center gap-1 text-accent-400 font-label font-semibold text-sm">
              <i className="ri-star-fill w-4 h-4 flex items-center justify-center" />
              {current.rating.toFixed(1)}
            </span>
            <span>{formatViews(current.views)}</span>
            <span className="hidden sm:inline">导演：{current.director}</span>
            <span className="hidden sm:inline">{current.duration}</span>
          </div>
          <div
            className="mt-7 flex flex-wrap items-center gap-3 cc-fade-up"
            style={{ animationDelay: "220ms" }}
          >
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-fill w-4 h-4 flex items-center justify-center" />
              查看详情
            </Link>
            <Link
              to={`/category/${current.kind}`}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-50/30 text-background-100 text-sm hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              更多{current.kind === "movie" ? "电影" : "剧集"}
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
          </div>
        </div>
      </div>

      {/* indicators */}
      <div className="absolute z-20 bottom-6 md:bottom-8 left-4 right-4 md:left-6 md:right-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {items.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到 ${item.title}`}
              onClick={() => {
                go(i);
                resetTimer();
              }}
              className={`h-1 rounded-full transition-all duration-300 cursor-pointer ${
                i === active ? "w-8 bg-accent-500" : "w-4 bg-background-50/40 hover:bg-background-50/70"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="font-label text-xs text-background-300 tabular-nums mr-1">
            {String(active + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
          </span>
          <button
            type="button"
            aria-label="上一张"
            onClick={() => {
              go(active - 1);
              resetTimer();
            }}
            className="w-9 h-9 rounded-full border border-background-50/30 text-background-100 flex items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-lg w-4 h-4 flex items-center justify-center" />
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => {
              go(active + 1);
              resetTimer();
            }}
            className="w-9 h-9 rounded-full border border-background-50/30 text-background-100 flex items-center justify-center hover:bg-background-50/15 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-lg w-4 h-4 flex items-center justify-center" />
          </button>
        </div>
      </div>
    </section>
  );
}